/**
 * Shared design tokens — single source of truth for colors, gradients, shadows, radii, transitions.
 * Import from here instead of hardcoding values in components.
 */

// ── Colors ──────────────────────────────────────────────────────────────────
export const COLORS = {
  // Brand
  gold: '#cbb26a',
  darkGold: '#a89048',
  goldLight: '#fbf4a1',
  // Text
  textPrimary: '#1a1a1a',
  textSecondary: '#666666',
  textMuted: '#888888',
  textPlaceholder: '#999999',
  // Surface
  white: '#ffffff',
  bgPage: '#f8fafc',
  bgCard: '#ffffff',
  bgModalOverlay: 'rgba(0, 0, 0, 0.5)',
  checkGreen: '#27ae60',
  // Semantic
  success: '#065f46',
  successBg: '#d1fae5',
  error: '#dc2626',
  errorBg: '#fef2f2',
  errorDark: '#b91c1c',
  warning: '#f39d00',
  warningBg: '#fff7e6',
  warningText: '#92400e',
  info: '#4a90e2',
  infoBg: '#e8f4fd',
  // Border
  border: '#f5f5f5',
  borderLight: 'rgba(255, 255, 255, 0.2)',
} as const;

// ── Gradients ───────────────────────────────────────────────────────────────
export const GRADIENTS = {
  gold: 'linear-gradient(92.83deg, #cbb26a -1.61%, #fbf4a1 53.52%, #cbb26a 101.51%)',
  goldSubtle: 'linear-gradient(135deg, #cbb26a 0%, #a89048 100%)',
  goldWithFallback: 'linear-gradient(92.83deg, #cbb26a -1.61%, #fbf4a1 53.52%, #cbb26a 101.51%), linear-gradient(0deg, #1a1a1a, #1a1a1a)',
  error: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
  statusOngoing: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
  statusCompleted: 'linear-gradient(135deg, #cbb26a 0%, #a89048 100%)',
  statusInactive: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
  modalBody: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  cardHeader: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
} as const;

// ── Shadows ─────────────────────────────────────────────────────────────────
export const SHADOWS = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.04)',
  card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.06)',
  cardHover: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  elevated: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)',
  goldButton: '0 2px 8px rgba(203, 178, 106, 0.3)',
  goldButtonHover: '0 4px 16px rgba(203, 178, 106, 0.4)',
  goldGlow: '0 2px 12px rgba(203, 178, 106, 0.25)',
  errorBox: '0 2px 8px rgba(220, 38, 38, 0.2)',
  successBox: '0 2px 8px rgba(6, 95, 70, 0.15)',
  infoBox: '0 2px 8px rgba(74, 144, 226, 0.15)',
  profileCard: '0 8px 32px rgba(0, 0, 0, 0.12)',
  modal: '0 -6px 18px rgba(0, 0, 0, 0.15)',
} as const;

// ── Border Radius ───────────────────────────────────────────────────────────
export const RADII = {
  sm: '6px',
  md: '8px',
  lg: '10px',
  xl: '12px',
  xxl: '16px',
  round: '50%',
  pill: '20px',
} as const;

// ── Transitions ─────────────────────────────────────────────────────────────
export const TRANSITIONS = {
  fast: 'all 0.2s ease',
  normal: 'all 0.3s ease',
  smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// ── Spacing scale (px, for inline styles) ───────────────────────────────────
export const SPACING = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

// ── Typography scale ───────────────────────────────────────────────────────
// De-facto sizes observed across the app: 10/11/12/13/14/16/18/20/24.
export const FONT_SIZES = {
  xxs: 10,
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 16,
  xl: 18,
  xxl: 20,
  xxxl: 24,
} as const;

export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  heavy: 800,
} as const;

// ── Z-index scale ──────────────────────────────────────────────────────────
// Values chosen to match what the app already uses:
//   BackHeaderBar 1000 · SocietySwitcherSheet 3050 · Sheet 4000 · ToastBox 10000
export const Z_INDEX = {
  base: 0,
  raised: 10,
  dropdown: 100,
  header: 1000,
  fab: 1200,
  backdrop: 3000,
  sheet: 4000,
  toast: 10000,
} as const;

// ── Neutral ramps ─────────────────────────────────────────────────────────
// The app hand-pastes Tailwind's grey + slate ramps inline (~3k literal hits).
// Tokenised here so future migrations have a target. Values are Tailwind's.
export const GREY = {
  50: '#f9fafb',
  100: '#f3f4f6',
  200: '#e5e7eb',
  300: '#d1d5db',
  400: '#9ca3af',
  500: '#6b7280',
  600: '#4b5563',
  700: '#374151',
  800: '#1f2937',
  900: '#111827',
} as const;

export const SLATE = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

